import { ref, unref, computed, watch } from 'vue'
import moment from 'moment'
import * as turf from '@turf/turf'
import { RestApiClient, TAG_USER_ROLE_ID } from 'ifield-core-components'
import store from '@/store'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { COLUMN_ID } from '@/composables/user/UseUserListColumnDefinition'
import {
  PLACE_RELATIONSHIP,
  STILL_DAYS_OPERATOR,
} from '@/composables/user/UseUserListFilterCondition'
import { ACTIVITY_CODE } from '@/constants'
import StringUtil from '@/js/util/StringUtil'

/**
 * 全て対象にする(フィルタしない)フィルタ関数
 */
const PASS_ALL = () => true

/**
 * キーワード検索項目時に対象となるフィールド
 * これと各種属性値が「すべての検索項目」時に対象になる
 */
const KEYWORD_TARGET_FIELDS = [
  'login_code',
  'user_name',
  'user_name_kana',
  'tel',
  'email',
  'remarks',
]

/**
 * カラム定義に準じて値を取得する
 * @param {RowNode} node 行ノードデータ
 * @param {Object} columnDef カラム定義
 * @returns {*} 値
 */
const getValueDefined = (node, columnDef) => {
  if (columnDef.valueGetter) {
    return columnDef.valueGetter(node)
  } else if (columnDef.field) {
    return node.data[columnDef.field]
  }
  return null
}

/**
 * プレイスをリモートからfetchする
 * @param {number} id プレイスID
 * @returns {Promise.<Object>} プレイスデータで解決するPromise
 */
const fetchPlace = async (id) => {
  const url = RestApiClient.createApiUrl(`place/${id}`)
  const json = await RestApiClient.executeGetApi(url)
  // 日時に関する情報は使用しないのでタイムゾーン変換していない
  return json.data
}

/**
 * ユーザーリストの絞り込み条件を絞り込み関数に変換する
 * @param {RefLike.<Object>} condition 絞り込み条件
 * @param {function} getBlankCondition 空の絞り込み条件を取得する関数
 * @param {MaybeRef.<ColDef[]>} columnDefs カラム定義
 * @param {MaybeRef.<Object>} context コンテキストデータ
 * @param {MaybeRef.<GridApi>} gridApi グリッド操作API
 * @param {Ref.<boolean>=} isLoading ローディングフラグ
 */
export const useUserListFilterFunction = ({
  condition,
  getBlankCondition,
  columnDefs,
  context,
  gridApi,
  isLoading,
}) => {
  /**
   * condition の各カテゴリ別フィルタ関数
   * ```
   * {
   *   [search|organization|indoor|place|status|activity|positioning]: (node: RowNode) => boolean
   * }
   * ```
   * @type {Ref.<Object>}
   */
  const filters = ref({})

  /**
   * filters更新
   */
  const updateFilters = async () => {
    const blankCondition = getBlankCondition()
    const entries = []
    for (const [key, value] of Object.entries(unref(condition) || {})) {
      // 条件が空とみなせる場合は全てパスする
      if (JSON.stringify(value) === JSON.stringify(blankCondition[key])) {
        entries.push([key, PASS_ALL])
        continue
      }

      switch (key) {
        case 'userType':
          entries.push([key, getUserTypeFilter(value)])
          break
        case 'search':
          entries.push([key, getSearchFilter(value)])
          break
        case 'organization':
          entries.push([key, getOrganizationFilter(value)])
          break
        case 'indoor':
          entries.push([key, getIndoorFilter(value)])
          break
        case 'place':
          entries.push([key, await getPlaceFilter(value)])
          break
        case 'status':
          entries.push([key, getStatusFilter(value)])
          break
        case 'activity':
          entries.push([key, getActivityFilter(value)])
          break
        case 'positioning':
          entries.push([key, getPositioningFilter(value)])
          break
        default:
          entries.push([key, PASS_ALL])
      }
    }
    filters.value = Object.fromEntries(entries)
  }

  /**
   * カラム定義のMap
   * @type {ComputedRef.<Map.<string, ColDef>>}
   */
  const columnDefMap = computed(
    () => new Map(unref(columnDefs).map((def) => [def.colId, def]))
  )
  /**
   * ユーザー属性マスタ
   * @type {ComputedRef.<string[]>}
   */
  const userAttributeNames = computed(() => [
    ...new Set(
      store.getters['userAttribute/userAttributes'].map(
        (attr) => attr.attribute_name
      )
    ),
  ])
  /**
   * 「すべての検索対象」の時のキーワード検索対象
   * @type {ComputedRef.<string[]>}
   */
  const allKeywordTargets = computed(() => [
    ...KEYWORD_TARGET_FIELDS,
    ...userAttributeNames.value,
  ])

  /**
   * ユーザー種別のフィルタリング関数を取得する
   * @param {string} target ユーザー種別
   * @returns {function}
   */
  const getUserTypeFilter = ({ target }) => {
    if (target == null) return PASS_ALL

    switch (target) {
      case 'without': // アプリユーザー
        return (node) =>
          node.data.role_id == null || node.data.role_id !== TAG_USER_ROLE_ID
      case 'only_rel': // タグユーザー
        return (node) => node.data.role_id === TAG_USER_ROLE_ID
      default:
        return PASS_ALL
    }
  }

  /**
   * キーワードのフィルタリング関数を取得する
   * @param {string} keyword キーワード
   * @param {string[]} keywordTargets キーワード検索対象項目
   * @returns {boolean}
   */
  const getSearchFilter = ({ keyword, keywordTargets }) => {
    if (keyword == null || keyword === '') return PASS_ALL

    const targets =
      keywordTargets == null || !keywordTargets.length
        ? allKeywordTargets.value
        : keywordTargets
    return (node) => {
      const texts = targets
        .flatMap((field) =>
          KEYWORD_TARGET_FIELDS.includes(field)
            ? node.data[field]
            : node.data.attributes
                .filter((attr) => attr.attribute_name === field)
                .map((attr) => attr.attribute_value)
        )
        .filter((v) => v)
      return texts.some((text) => StringUtil.testByKeyword(text, keyword))
    }
  }

  /**
   * 組織のフィルタリング関数を取得する
   * @param {number[]} organizationIds 対象組織ID
   * @returns {function}
   */
  const getOrganizationFilter = ({ organizationIds }) => {
    return (node) =>
      (
        getValueDefined(node, columnDefMap.value.get(COLUMN_ID.ORGANIZATION)) ||
        []
      ).some((g) => organizationIds.includes(g.id))
  }

  /**
   * 組織のフィルタリング関数を取得する
   * @param {number[]} indoorBuildingIds 対象ビルID
   * @param {number} indoorLevelId 対象フロアID
   * @returns {function}
   */
  const getIndoorFilter = ({ indoorBuildingIds, indoorLevelId }) => {
    if (indoorLevelId) {
      return (node) => node.data.indoor_level_id === indoorLevelId
    } else if (indoorBuildingIds) {
      return (node) => indoorBuildingIds.includes(node.data.indoor_building_id)
    } else {
      return PASS_ALL
    }
  }

  const { handleApiError } = useHandleApiError()
  /**
   * 取得したプレイスデータを保持するMap
   * @type {Map.<number, Object>}
   */
  const placeMap = new Map()
  /**
   * プレイスを取得する
   * @param {number} id プレイスID
   * @returns {Promise.<Object>} プレイスデータで解決するPromise
   */
  const getPlace = async (id) => {
    if (placeMap.has(id)) {
      return placeMap.get(id)
    }

    if (isLoading) isLoading.value = true
    let data
    try {
      data = await fetchPlace(id)
    } catch (error) {
      handleApiError(error)
    } finally {
      if (isLoading) isLoading.value = false
    }

    if (data) {
      placeMap.set(data.id, data)
    }
    return data
  }

  /**
   * プレイスのフィルタリング関数を取得する
   * @param {number} placeId プレイスID
   * @param {PLACE_RELATIONSHIP} relationship 対象プレイスとの関係性
   * @param {number} distance プレイスからの距離(m)
   * @returns {function}
   */
  const getPlaceFilter = async ({ placeId, relationship, distance }) => {
    if (!placeId) return PASS_ALL

    switch (relationship) {
      case PLACE_RELATIONSHIP.IS:
        return (node) => node.data.place_id === placeId
      case PLACE_RELATIONSHIP.CHECKIN:
        // COLUMN_ID.CHECKIN の値はチェックイン時 true の boolean なのでそのままフィルタ関数に使用できる
        return (node) =>
          node.data.checkin_place_id === placeId &&
          getValueDefined(node, columnDefMap.value.get(COLUMN_ID.CHECKIN))
      case PLACE_RELATIONSHIP.DISTANCE: {
        const place = await getPlace(placeId)
        const pGeom = place.geom?.geometry
        if (!pGeom) return () => false

        switch (pGeom.type) {
          case 'Point': {
            return (node) =>
              turf.distance(pGeom, node.data.geom, { units: 'meters' }) <=
              distance
          }
          case 'LineString': {
            return (node) =>
              turf.pointToLineDistance(node.data.geom, pGeom, {
                units: 'meters',
              }) <= distance
          }
          case 'Polygon': {
            return (node) => {
              // 交差している or 指定距離以内
              if (turf.booleanIntersects(pGeom, node.data.geom)) return true
              const minDistance = turf.segmentReduce(
                pGeom,
                (value, segment) =>
                  Math.min(
                    value,
                    turf.pointToLineDistance(node.data.geom, segment, {
                      units: 'meters',
                    })
                  ),
                Math.Infinity
              )
              return minDistance <= distance
            }
          }
          default:
            return () => false
        }
      }
      default:
        return PASS_ALL
    }
  }

  /**
   * ステイタスのフィルタリング関数を取得する
   * @param {number} statusTypeId ステイタス種別ID
   * @returns {function}
   */
  const getStatusFilter = ({ statusTypeId }) => {
    return (node) => node.data.current_status_type_id === statusTypeId
  }

  /**
   * 稼働状態のフィルタリング関数を取得する
   * @param {boolean} isStill 停止中のみの場合はtrue
   * @param {boolean} canSetDays 停止日数を指定可能な場合はtrue
   * @param {number} stillDays 停止期間(日)
   * @param {STILL_DAYS_OPERATOR} stillDaysOperator 停止期間演算子(ge: 以上, le: 以内)
   * @returns {function}
   */
  const getActivityFilter = ({
    isStill,
    canSetDays,
    stillDays,
    stillDaysOperator,
  }) => {
    // すべて
    if (isStill == null) return PASS_ALL
    // 稼働中
    if (isStill === false) {
      return (node) => {
        const activity = getValueDefined(
          node,
          columnDefMap.value.get(COLUMN_ID.ACTIVITY)
        )
        return activity != null && activity !== ACTIVITY_CODE.STILL
      }
    }

    const needsDateFilter =
      isStill && canSetDays && stillDays != null && stillDaysOperator
    // 停止中で日数指定が無効
    if (!needsDateFilter) {
      return (node) =>
        getValueDefined(node, columnDefMap.value.get(COLUMN_ID.ACTIVITY)) ===
        ACTIVITY_CODE.STILL
    }

    return (node) => {
      const activity = getValueDefined(
        node,
        columnDefMap.value.get(COLUMN_ID.ACTIVITY)
      )
      if (activity !== ACTIVITY_CODE.STILL) return false

      const changeAt = moment(node.data.activity_change_at)
      const targetAt = moment(unref(context).updateDate).subtract(
        +stillDays,
        'days'
      )
      if (stillDaysOperator === STILL_DAYS_OPERATOR.GREATER) {
        return targetAt - changeAt >= 0
      } else if (stillDaysOperator === STILL_DAYS_OPERATOR.LESS) {
        return targetAt - changeAt <= 0
      } else {
        return false
      }
    }
  }

  /**
   * 測位状態のフィルタリング関数を取得する
   * @param {boolean} isPositioning 測位状態
   * @param {ColDef[]} columnDefs カラム定義
   * @returns {function}
   */
  const getPositioningFilter = ({ isPositioning }) => {
    return (node) =>
      getValueDefined(
        { ...node, context: unref(context) },
        columnDefMap.value.get(COLUMN_ID.POSITIONING)
      ) === isPositioning
  }

  // condition変更時に _filters を更新
  watch(() => [condition, columnDefs, context], updateFilters, {
    immediate: true,
    deep: true,
  })
  /**
   * 何らか効力のあるフィルタ関数が設定されていれば true を返す関数
   * ag-grid の isExternalFilterPresent に渡す想定
   * @type {ComputedRef.<function>}
   */
  const isFilterPresent = () =>
    Object.values(filters.value).some((f) => f !== PASS_ALL)

  /**
   * RowNodeを引数として対象行が表示対象かどうかを返す関数
   * ag-grid の doesExternalFilterPass に渡す想定
   * @type {ComputedRef.<function>}
   */
  const filterFunction = (node) =>
    Object.values(filters.value).every((f) => f(node))

  // grid にフィルタの再評価を伝える
  watch(filters, () => unref(gridApi)?.onFilterChanged())

  return { isFilterPresent, filterFunction }
}
